import React, { useState, useEffect } from 'react'
import type { Course } from '@/types/course'
import type { Affiliate } from '@/types/affiliate'

interface CourseCommissionModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: {
        affiliateId: string
        courseId: string
        commissionType: 'percentage' | 'fixed'
        commissionValue: number
    }) => Promise<void>
    mode: 'by-affiliate' | 'by-course'
    // For by-affiliate mode: show course selector, affiliateId is fixed
    affiliateId?: string
    courses?: Course[]
    // For by-course mode: show affiliate selector, courseId is fixed
    courseId?: string
    affiliates?: Affiliate[]
    // For edit mode
    editingCommission?: {
        affiliateId: string
        courseId: string
        commissionType: 'percentage' | 'fixed'
        commissionValue: number
    }
    editingDefault?: boolean
    currentCourse?: Course | null
}

export const CourseCommissionModal: React.FC<CourseCommissionModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    mode,
    affiliateId,
    courses,
    courseId,
    affiliates,
    editingCommission,
    editingDefault,
    currentCourse,
}) => {
    const [selectedAffiliateId, setSelectedAffiliateId] = useState('')
    const [selectedCourseId, setSelectedCourseId] = useState('')
    const [commissionType, setCommissionType] = useState<'percentage' | 'fixed'>('percentage')
    const [commissionValue, setCommissionValue] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (isOpen) {
            if (editingDefault && currentCourse) {
                // Editing default commission
                setSelectedAffiliateId('ALL')
                setSelectedCourseId(courseId || '')
                setCommissionType(currentCourse.defaultCommissionType || 'percentage')
                setCommissionValue(currentCourse.defaultCommissionValue?.toString() || '')
            } else if (editingCommission) {
                // Editing specific affiliate commission
                setSelectedAffiliateId(editingCommission.affiliateId)
                setSelectedCourseId(editingCommission.courseId)
                setCommissionType(editingCommission.commissionType)
                setCommissionValue(editingCommission.commissionValue.toString())
            } else {
                // Creating new commission
                setSelectedAffiliateId(affiliateId || '')
                setSelectedCourseId(courseId || '')
                setCommissionType('percentage')
                setCommissionValue('')
            }
            setError('')
        }
    }, [isOpen, affiliateId, courseId, editingCommission, editingDefault, currentCourse])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        const value = parseFloat(commissionValue)
        if (isNaN(value) || value <= 0) {
            setError('Commission value must be greater than 0')
            return
        }

        if (commissionType === 'percentage' && value > 100) {
            setError('Percentage cannot exceed 100%')
            return
        }

        const finalAffiliateId = mode === 'by-affiliate' ? affiliateId! : selectedAffiliateId
        const finalCourseId = mode === 'by-course' ? courseId! : selectedCourseId

        if (!finalAffiliateId || !finalCourseId) {
            setError('Please select all required fields')
            return
        }

        setIsSubmitting(true)
        try {
            await onSubmit({
                affiliateId: finalAffiliateId,
                courseId: finalCourseId,
                commissionType,
                commissionValue: value,
            })
            onClose()
        } catch (err: any) {
            setError(err.message || 'Failed to save commission')
        } finally {
            setIsSubmitting(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {editingDefault
                            ? 'Edit Default Commission'
                            : editingCommission
                            ? 'Edit Course Commission'
                            : 'Set Course Commission'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Course Selector (for by-affiliate mode) */}
                    {mode === 'by-affiliate' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-text-muted mb-2">
                                Course
                            </label>
                            <select
                                value={selectedCourseId}
                                onChange={(e) => setSelectedCourseId(e.target.value)}
                                disabled={!!editingCommission}
                                className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary/50 disabled:opacity-50"
                                required
                            >
                                <option value="">Select a course</option>
                                {courses?.map((course) => (
                                    <option key={course.id} value={course.id}>
                                        {course.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Affiliate Selector (for by-course mode) */}
                    {mode === 'by-course' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-500 dark:text-text-muted mb-2">
                                Affiliate
                            </label>
                            <select
                                value={selectedAffiliateId}
                                onChange={(e) => setSelectedAffiliateId(e.target.value)}
                                disabled={!!editingCommission || editingDefault}
                                className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary/50 disabled:opacity-50"
                                required
                            >
                                <option value="">Select an affiliate</option>
                                <option value="ALL" className="font-bold">🌐 ALL AFFILIATES (Default)</option>
                                <option disabled>──────────</option>
                                {affiliates?.map((affiliate) => (
                                    <option key={affiliate.id} value={affiliate.id}>
                                        {affiliate.code}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Commission Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-text-muted mb-2">
                            Commission Type
                        </label>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setCommissionType('percentage')}
                                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                                    commissionType === 'percentage'
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                Percentage
                            </button>
                            <button
                                type="button"
                                onClick={() => setCommissionType('fixed')}
                                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                                    commissionType === 'fixed'
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                Fixed Amount
                            </button>
                        </div>
                    </div>

                    {/* Commission Value */}
                    <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-text-muted mb-2">
                            Commission Value
                        </label>
                        <div className="relative">
                            {commissionType === 'fixed' && (
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                                    Rp
                                </span>
                            )}
                            <input
                                type="number"
                                value={commissionValue}
                                onChange={(e) => setCommissionValue(e.target.value)}
                                step={commissionType === 'percentage' ? '0.01' : '1000'}
                                min="0"
                                max={commissionType === 'percentage' ? '100' : undefined}
                                placeholder={commissionType === 'percentage' ? '0.00' : '0'}
                                className={`w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary/50 ${
                                    commissionType === 'fixed' ? 'pl-12' : ''
                                }`}
                                required
                            />
                            {commissionType === 'percentage' && (
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                                    %
                                </span>
                            )}
                        </div>
                        {commissionType === 'percentage' && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-text-muted">
                                Enter value between 0 and 100
                            </p>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-sm text-red-400">{error}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Commission'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
